// src/utils/timeFormat.js

/**
 * Format a UTC timestamp to local time based on user preferences
 * @param {Date|string} utcTimestamp - UTC timestamp
 * @param {object} userPreferences - User's time and date format preferences
 * @param {string} userPreferences.time_format - "12" or "24" 
 * @param {string} userPreferences.date_format - Date format like "MM/DD/YYYY", "DD/MM/YYYY", etc.
 * @param {string} userPreferences.timezone - User's timezone (optional)
 * @returns {string} Formatted local date and time string
 */
/**
 * Convert GMT offset to valid IANA timezone identifier
 * @param {string} gmtOffset - GMT offset like "GMT+02", "GMT-05", etc.
 * @returns {string} Valid IANA timezone identifier
 */
const convertGMTOffsetToTimezone = (gmtOffset) => {
  if (!gmtOffset || !gmtOffset.startsWith('GMT')) {
    return gmtOffset; // Return as-is if not GMT format
  }
  
  // Map common GMT offsets to IANA timezones
  const gmtToTimezone = {
    'GMT-12': 'Pacific/Kwajalein',
    'GMT-11': 'Pacific/Midway',
    'GMT-10': 'Pacific/Honolulu',
    'GMT-09': 'America/Anchorage',
    'GMT-08': 'America/Los_Angeles',
    'GMT-07': 'America/Denver',
    'GMT-06': 'America/Chicago',
    'GMT-05': 'America/New_York',
    'GMT-04': 'America/Halifax',
    'GMT-03': 'America/Sao_Paulo',
    'GMT-02': 'Atlantic/South_Georgia',
    'GMT-01': 'Atlantic/Azores',
    'GMT+00': 'UTC',
    'GMT+01': 'Europe/London',
    'GMT+02': 'Europe/Berlin',
    'GMT+03': 'Europe/Moscow',
    'GMT+04': 'Asia/Dubai',
    'GMT+05': 'Asia/Karachi',
    'GMT+06': 'Asia/Dhaka',
    'GMT+07': 'Asia/Bangkok',
    'GMT+08': 'Asia/Shanghai',
    'GMT+09': 'Asia/Tokyo',
    'GMT+10': 'Australia/Sydney',
    'GMT+11': 'Pacific/Norfolk',
    'GMT+12': 'Pacific/Auckland'
  };
  
  return gmtToTimezone[gmtOffset] || 'UTC';
};

export const formatUserTime = (utcTimestamp, userPreferences = {}) => {
  if (!utcTimestamp) return '';
  
  const date = new Date(utcTimestamp);
  
  // Default preferences - handle both string and number formats
  const timeFormat = String(userPreferences.time_format || '12');
  const dateFormat = userPreferences.date_format || 'MM/DD/YYYY';
  const rawTimezone = userPreferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezone = convertGMTOffsetToTimezone(rawTimezone);
  
  // Create formatter options
  const options = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat !== '24' // false for 24-hour format, true for 12-hour
  };
  
  try {
    // Use appropriate locale for 24-hour format
    const locale = timeFormat === '24' ? 'en-GB' : 'en-US';
    const formatter = new Intl.DateTimeFormat(locale, options);
    const formattedParts = formatter.formatToParts(date);
    
    // Extract parts
    const parts = {};
    formattedParts.forEach(part => {
      parts[part.type] = part.value;
    });
    
    // Get month name for formats that use it
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    const monthName = monthNames[parseInt(parts.month) - 1];
    
    // Format date according to user preference
    let formattedDate;
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        formattedDate = `${parts.day}/${parts.month}/${parts.year}`;
        break;
      case 'YYYY-MM-DD':
        formattedDate = `${parts.year}-${parts.month}-${parts.day}`;
        break;
      case 'DD-MM-YYYY':
        formattedDate = `${parts.day}-${parts.month}-${parts.year}`;
        break;
      case 'MM-DD-YYYY':
        formattedDate = `${parts.month}-${parts.day}-${parts.year}`;
        break;
      case 'DD MMM YYYY':
        formattedDate = `${parts.day} ${monthName} ${parts.year}`;
        break;
      case 'MMM DD, YYYY':
        formattedDate = `${monthName} ${parts.day}, ${parts.year}`;
        break;
      case 'MM/DD/YYYY':
      default:
        formattedDate = `${parts.month}/${parts.day}/${parts.year}`;
        break;
    }
    
    // Format time - in 24-hour format, dayPeriod might not exist
    let timeString;
    if (timeFormat === '24' || !parts.dayPeriod) {
      timeString = `${parts.hour}:${parts.minute}`;
    } else {
      timeString = `${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
    }
    
    return `${formattedDate} ${timeString}`;
    
  } catch (error) {
    console.error('Error formatting time:', error);
    // Fallback to simple local string
    return date.toLocaleString();
  }
};

/**
 * Format a UTC timestamp for relative time (like "2 hours ago")
 * @param {Date|string} utcTimestamp - UTC timestamp
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (utcTimestamp) => {
  if (!utcTimestamp) return '';
  
  // Ensure the timestamp is treated as UTC
  let date;
  if (typeof utcTimestamp === 'string' && !utcTimestamp.endsWith('Z')) {
    // If it's a string without Z, append it to ensure UTC parsing
    date = new Date(utcTimestamp + 'Z');
  } else {
    date = new Date(utcTimestamp);
  }
  
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / 60000);
  
  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInMinutes < 1440) { // Less than 24 hours
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours}h ago`;
  } else if (diffInMinutes < 10080) { // Less than 7 days
    const days = Math.floor(diffInMinutes / 1440);
    return `${days}d ago`;
  } else {
    // For older messages, show the actual date
    return date.toLocaleDateString();
  }
};

/**
 * Format date only (no time) based on user preferences
 * @param {Date|string} dateValue - Date value
 * @param {object} userPreferences - User's date format preferences
 * @param {string} userPreferences.date_format - Date format like "MM/DD/YYYY", "DD/MM/YYYY", etc.
 * @returns {string} Formatted date string
 */
export const formatUserDate = (dateValue, userPreferences = {}) => {
  if (!dateValue) return '';
  
  const date = new Date(dateValue);
  const dateFormat = userPreferences.date_format || 'MM/DD/YYYY';
  
  // Handle various date format preferences
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  // Get month name for formats that use it
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  const monthName = monthNames[date.getMonth()];
  
  switch (dateFormat) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'MM-DD-YYYY':
      return `${month}-${day}-${year}`;
    case 'DD MMM YYYY':
      return `${day} ${monthName} ${year}`;
    case 'MMM DD, YYYY':
      return `${monthName} ${day}, ${year}`;
    case 'MM/DD/YYYY':
    default:
      return `${month}/${day}/${year}`;
  }
};

/**
 * Format time for display in discussions - shows relative time with tooltip of absolute time
 * @param {Date|string} utcTimestamp - UTC timestamp
 * @param {object} userPreferences - User's time and date format preferences
 * @returns {object} Object with display text and tooltip text
 */
export const formatDiscussionTime = (utcTimestamp, userPreferences = {}) => {
  const relativeTime = formatRelativeTime(utcTimestamp);
  const absoluteTime = formatUserTime(utcTimestamp, userPreferences);
  
  return {
    display: relativeTime,
    tooltip: absoluteTime
  };
};